import type { QueryClient } from "@tanstack/react-query";

type PlanCacheScope = {
	/** lines whose plan moved; omit to invalidate every line */
	assetIds?: (number | null | undefined)[];
	/** work order grids and the pending badge counts */
	workOrders?: boolean;
	/** the undo/redo entry */
	planChange?: boolean;
	/** saw reclassify product list, derived from the work orders */
	sawReclassify?: boolean;
	/** break rules and the per-shift break counts the Day Config shows */
	breaks?: boolean;
};

const scoped = (key: string, assetId?: number) =>
	assetId == null ? [key] : [key, assetId];

/**
 * The month planner and the day display share the calendarEvents cache, so one
 * plan edit has to reach both plus whatever grid derives from it. Every caller
 * that moves a plan goes through here — the alternative is four copies of the
 * key list drifting apart.
 */
export const invalidatePlanQueries = (
	queryClient: QueryClient,
	{
		assetIds,
		workOrders,
		planChange,
		sawReclassify,
		breaks,
	}: PlanCacheScope = {},
) => {
	const ids = [...new Set(assetIds?.filter((id): id is number => id != null))];
	const targets = ids.length ? ids : [undefined];
	const invalidate = (queryKey: unknown[]) =>
		queryClient.invalidateQueries({ queryKey });

	const pending = targets.flatMap((id) => [
		invalidate(scoped("calendarEvents", id)),
		...(workOrders ? [invalidate(scoped("workOrders", id))] : []),
		...(planChange ? [invalidate(scoped("lastPlanChange", id))] : []),
	]);

	if (workOrders) pending.push(invalidate(["workOrderPendingCounts"]));
	if (sawReclassify) pending.push(invalidate(["sawReclassifyProducts"]));
	if (breaks)
		pending.push(invalidate(["breakRules"]), invalidate(["dayShiftOptions"]));

	return Promise.all(pending);
};
